import { Loader } from "@certn/cascadia-react";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ApplicationContext } from "./Application.context";
import { Section, SectionKey } from "./Section";
import { Summary } from "./Summary";
import { useSectionNavigation } from "./hooks";
import { PageNotFoundError } from "../../components/PageNotFoundError";
import { useDocumentTitle } from "../../hooks/api/useDocumentTitle";

export function ApplicationPage() {
  const { t } = useTranslation();
  useDocumentTitle(`Certn ${t("Application")}`);
  const { sections, currentSectionKey } = useContext(ApplicationContext);

  const { navigateToSection, navigateToSummary } = useSectionNavigation();

  const { sectionName } = useParams();

  const currentSection = sections.find(
    (section) => section.key === currentSectionKey
  );

  const currentSectionIndex = sections.findIndex(
    (section) => section.key === currentSectionKey
  );

  useEffect(() => {
    if (!sectionName || currentSection?.isDisabled) {
      const nextSection = sections.find(
        (section) => !section.isCompleted && section.key !== currentSection?.key
      );

      navigateToSection(nextSection?.key || SectionKey.SUMMARY);
    }
  }, [currentSection]);

  if (currentSectionKey === SectionKey.SUMMARY) {
    return <Summary sections={sections} />;
  }

  if (!sectionName) {
    return <Loader data-testid="loader" overlay />;
  }

  if (!currentSection) {
    return <PageNotFoundError />;
  }

  const handleNext = async () => {
    if (currentSectionIndex < sections.length - 1) {
      await navigateToSection(sections[currentSectionIndex + 1].key);
      return;
    }

    await navigateToSummary();
  };

  const handleBack = async () => {
    await navigateToSection(sections[currentSectionIndex - 1].key);
  };

  return (
    <Section
      key={currentSectionKey}
      section={currentSection}
      onBack={currentSectionIndex > 0 ? handleBack : undefined}
      onNext={handleNext}
    />
  );
}
